<template>
  <section
    v-if="features.length"
    id="features"
    class="lpu-features d-flex flex-column"
    :class="isMobile ? 'ga-5' : 'ga-8'"
    data-qa="lpu_features"
  >
    <h2
      ref="titleRef"
      data-qa="lpu_features_title"
      class="text--text"
      :class="isMobile ? 'text-h6' : 'text-h5'"
    >
      {{ tStatic('Особенности клиники') }}
    </h2>

    <div class="d-flex flex-column ga-6">
      <div class="lpu-features__lists d-flex flex-column ga-6">
        <div
          :class="isMobile ? 'd-flex flex-column ga-6' : 'lpu-features__list--desktop'"
        >
          <FeatureItem
            v-for="feature in collapsedFeatures"
            :key="feature.key"
            :feature="feature"
          />
        </div>

        <v-expand-transition>
          <div
            v-if="isExpanded && expandedFeatures.length"
            :class="isMobile ? 'd-flex flex-column ga-6' : 'lpu-features__list--desktop'"
          >
            <FeatureItem
              v-for="feature in expandedFeatures"
              :key="feature.key"
              :feature="feature"
            />
          </div>
        </v-expand-transition>
      </div>

      <button
        v-if="hasHiddenFeatures"
        type="button"
        class="lpu-features__toggle align-self-start d-inline-flex align-center ga-3 text-body-1"
        :class="ff_lpu_branding_page_v5
          ? 'text-lpu-branding-color-text'
          : 'text-lpu-branding-color-primary'"
        data-qa="lpu_features_toggle"
        @click="toggleExpanded"
      >
        <span>
          {{ isExpanded
            ? tStatic('Свернуть все особенности')
            : tStatic('Показать все особенности') }}
        </span>
        <span
          class="ui-icon-arrow-down ui-icon-fz-big flex-shrink-0"
          :class="{ 'lpu-features__toggle-icon--expanded': isExpanded }"
        />
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'
import useIsMobile from 'src/composables/useIsMobile'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import getLpuFeatures from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuFeatures/functions/getLpuFeatures'
import type { LpuInformation } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import FeatureItem from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuFeatures/components/FeatureItem.vue'

const props = defineProps<{
  lpuInformation?: LpuInformation | null
}>()

const { context }: CommonContextStore = useContextStore()
const ff_lpu_branding_page_v5 = computed(() => (
  Boolean(context.request.feature_flags.ff_lpu_branding_page_v5)
))
const isMobile = useIsMobile()
const isExpanded = ref(false)
const titleRef = ref<HTMLElement>()

const LPU_FEATURES_COLLAPSED_COUNT = 6

const features = computed(() => getLpuFeatures(props.lpuInformation))
const hasHiddenFeatures = computed(() => features.value.length > LPU_FEATURES_COLLAPSED_COUNT)

const collapsedFeatures = computed(() => (
  hasHiddenFeatures.value
    ? features.value.slice(0, LPU_FEATURES_COLLAPSED_COUNT)
    : features.value
))
const expandedFeatures = computed(() => (
  hasHiddenFeatures.value
    ? features.value.slice(LPU_FEATURES_COLLAPSED_COUNT)
    : []
))

async function toggleExpanded() {
  isExpanded.value = !isExpanded.value

  if (!isExpanded.value && isMobile.value) {
    await nextTick()
    titleRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}
</script>

<style scoped lang="scss">
.lpu-features {
  /* Компенсирует row-gap во время enter/leave expand-transition */
  &__lists > :deep(.expand-transition-leave-active),
  &__lists > :deep(.expand-transition-enter) {
    margin-bottom: -24px;
  }

  &__list--desktop {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px 0;
  }

  &__toggle {
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
  }

  &__toggle-icon--expanded {
    transform: rotate(180deg);
  }
}
</style>
